import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { isSupportedLocale, pickDefaultLocale } from "@/i18n/locale-utils";
import { RequireRole } from "@/components/auth/RequireRole";

/**
 * Admin routes require both:
 *   1. app_role = 'admin' (delegated to RequireRole)
 *   2. Authenticator Assurance Level 2 (TOTP step-up)
 *
 * If the admin lacks AAL2 we redirect to /{locale}/settings/security with
 * ?reason=admin_mfa_required so the security page can prompt enrollment
 * and/or step-up challenge.
 */
function AalGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<"loading" | "ok" | "needs_mfa">("loading");
  const { pathname, search, hash } = useLocation();
  const { locale: paramLocale } = useParams<{ locale: string }>();
  const locale = isSupportedLocale(paramLocale) ? paramLocale : pickDefaultLocale();

  useEffect(() => {
    let mounted = true;
    (async () => {
      // E2E-only escape hatch — off by default. When set at build time, skip
      // the MFA challenge so Playwright admin specs can exercise admin UIs
      // without enrolling TOTP for the seeded admin user.
      if (import.meta.env.VITE_E2E_BYPASS_ADMIN_MFA === "1") {
        if (mounted) setState("ok");
        return;
      }
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (!mounted) return;
      if (error) {
        setState("needs_mfa");
        return;
      }
      setState(data?.currentLevel === "aal2" ? "ok" : "needs_mfa");
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (state === "loading") {
    return (
      <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (state === "needs_mfa") {
    const next = encodeURIComponent(`${pathname}${search}${hash}`);
    return (
      <Navigate
        to={`/${locale}/settings/security?reason=admin_mfa_required&next=${next}`}
        replace
      />
    );
  }
  return <>{children}</>;
}

export function RequireAdminMfa({ children }: { children: ReactNode }) {
  return (
    <RequireRole role="admin" onDeny="notFound">
      <AalGate>{children}</AalGate>
    </RequireRole>
  );
}